import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
} from 'firebase/firestore';
import type { DocumentData, Firestore } from 'firebase/firestore';
import { STAKKO_MAX_EARN_PER_CALL, STAKKO_WELCOME_CREDITS } from '../config';
import type {
  StakkoAppId,
  StakkoWallet,
  StakkoWalletTransaction,
} from '../types';

const EMPTY_WALLET: StakkoWallet = {
  balance: 0,
  lifetimeEarned: 0,
  lifetimeSpent: 0,
};

function walletFromData(data: DocumentData | undefined): StakkoWallet {
  if (!data) return EMPTY_WALLET;
  return {
    balance: typeof data.balance === 'number' ? data.balance : 0,
    lifetimeEarned: typeof data.lifetimeEarned === 'number' ? data.lifetimeEarned : 0,
    lifetimeSpent: typeof data.lifetimeSpent === 'number' ? data.lifetimeSpent : 0,
    updatedAt: data.updatedAt,
  };
}

function walletRef(db: Firestore, uid: string) {
  return doc(db, 'vibiz_wallets', uid);
}

function transactionsRef(db: Firestore, uid: string) {
  return collection(db, 'vibiz_wallets', uid, 'transactions');
}

/**
 * Vytvoří peněženku s uvítacím bonusem, pokud ještě neexistuje.
 * Nikdy nepřepisuje existující zůstatek.
 */
export async function ensureStakkoWallet(db: Firestore, uid: string): Promise<void> {
  const ref = walletRef(db, uid);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (snap.exists()) return;

    tx.set(ref, {
      balance: STAKKO_WELCOME_CREDITS,
      lifetimeEarned: STAKKO_WELCOME_CREDITS,
      lifetimeSpent: 0,
      lastDelta: STAKKO_WELCOME_CREDITS,
      updatedAt: serverTimestamp(),
    });
    tx.set(doc(transactionsRef(db, uid)), {
      delta: STAKKO_WELCOME_CREDITS,
      reason: 'welcome',
      appId: null,
      meta: null,
      createdAt: serverTimestamp(),
    });
  });
}

export function subscribeStakkoWallet(
  db: Firestore,
  uid: string,
  onData: (wallet: StakkoWallet) => void,
): () => void {
  return onSnapshot(
    walletRef(db, uid),
    (snap) => onData(walletFromData(snap.data())),
    (error) => {
      console.warn('[stakko] peněženku se nepodařilo načíst', error);
      onData(EMPTY_WALLET);
    },
  );
}

export function subscribeStakkoWalletTransactions(
  db: Firestore,
  uid: string,
  onData: (transactions: StakkoWalletTransaction[]) => void,
): () => void {
  const q = query(transactionsRef(db, uid), orderBy('createdAt', 'desc'), limit(30));
  return onSnapshot(
    q,
    (snap) => {
      onData(
        snap.docs.map((item) => {
          const data = item.data();
          return {
            id: item.id,
            delta: typeof data.delta === 'number' ? data.delta : 0,
            reason: typeof data.reason === 'string' ? data.reason : 'unknown',
            appId: (data.appId ?? undefined) as StakkoAppId | undefined,
            meta: (data.meta ?? undefined) as Record<string, unknown> | undefined,
            createdAt: data.createdAt ?? null,
          };
        }),
      );
    },
    (error) => {
      console.warn('[stakko] historii kreditů se nepodařilo načíst', error);
      onData([]);
    },
  );
}

export class StakkoInsufficientCreditsError extends Error {
  constructor(public readonly required: number, public readonly balance: number) {
    super(`INSUFFICIENT_CREDITS:${required}:${balance}`);
    this.name = 'StakkoInsufficientCreditsError';
  }
}

/**
 * Utratí kredity. Probíhá v transakci, takže se nedá dostat do záporného
 * zůstatku ani při dvou souběžných požadavcích.
 */
export async function spendStakkoCredits(
  db: Firestore,
  uid: string,
  amount: number,
  reason: string,
  appId?: StakkoAppId,
  meta?: Record<string, unknown>,
): Promise<StakkoWallet> {
  const safeAmount = Math.max(0, Math.round(amount));
  if (safeAmount === 0) {
    throw new Error('INVALID_AMOUNT');
  }

  const ref = walletRef(db, uid);
  const result = await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const current = walletFromData(snap.data());

    if (current.balance < safeAmount) {
      throw new StakkoInsufficientCreditsError(safeAmount, current.balance);
    }

    const next: StakkoWallet = {
      balance: current.balance - safeAmount,
      lifetimeEarned: current.lifetimeEarned,
      lifetimeSpent: current.lifetimeSpent + safeAmount,
    };

    tx.set(
      ref,
      {
        ...next,
        lastDelta: -safeAmount,
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
    tx.set(doc(transactionsRef(db, uid)), {
      delta: -safeAmount,
      reason,
      appId: appId ?? null,
      meta: meta ?? null,
      createdAt: serverTimestamp(),
    });

    return next;
  });

  return result;
}

/**
 * Připíše kredity (odměna za aktivitu, referral, sdílený materiál).
 * Pro platby kartou používej serverovou Cloud Function – klient nesmí být
 * jediný zdroj pravdy o zaplacených kreditech.
 */
export async function earnStakkoCredits(
  db: Firestore,
  uid: string,
  amount: number,
  reason: string,
  appId?: StakkoAppId,
  meta?: Record<string, unknown>,
): Promise<StakkoWallet> {
  const safeAmount = Math.min(STAKKO_MAX_EARN_PER_CALL, Math.max(0, Math.round(amount)));
  if (safeAmount === 0) {
    throw new Error('INVALID_AMOUNT');
  }

  const ref = walletRef(db, uid);
  const result = await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const current = walletFromData(snap.data());

    const next: StakkoWallet = {
      balance: current.balance + safeAmount,
      lifetimeEarned: current.lifetimeEarned + safeAmount,
      lifetimeSpent: current.lifetimeSpent,
    };

    tx.set(
      ref,
      {
        ...next,
        lastDelta: safeAmount,
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
    tx.set(doc(transactionsRef(db, uid)), {
      delta: safeAmount,
      reason,
      appId: appId ?? null,
      meta: meta ?? null,
      createdAt: serverTimestamp(),
    });

    return next;
  });

  return result;
}
